import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  ArrowRight, ArrowLeft, CheckCircle, X,
  Sparkles, Building2, Award, Globe
} from 'lucide-react';
import axios from 'axios';
import {
  WelcomeStep,
  ProfileStep,
  PlansStep,
  WordPressStep,
  CompleteStep
} from './onboarding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingWizard = ({ user, open, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const navigate = useNavigate();

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to MyMedMember! 🎉',
      icon: Sparkles,
      component: WelcomeStep
    },
    {
      id: 'profile',
      title: 'Complete Your Profile',
      icon: Building2,
      component: ProfileStep
    },
    {
      id: 'plans',
      title: 'Create Your First Membership Plan',
      icon: Award,
      component: PlansStep
    },
    {
      id: 'wordpress',
      title: 'WordPress Integration',
      icon: Globe,
      component: WordPressStep
    },
    {
      id: 'complete',
      title: 'You\'re All Set!',
      icon: CheckCircle,
      component: CompleteStep
    }
  ];

  useEffect(() => {
    if (open) {
      fetchOnboardingStatus();
    }
  }, [open]);

  const fetchOnboardingStatus = async () => {
    try {
      // Get current user's clinic info
      const response = await axios.get(`${API}/clinic/info`);
      setOnboardingData(response.data);
      
      // Determine which step to start on based on completion status
      // This would be enhanced with actual onboarding checklist from DB
      setCurrentStep(0);
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
      // Continue anyway - wizard can work without full clinic data
      setOnboardingData({ subdomain: user.clinic_id });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    // Mark onboarding as complete (stored in localStorage)
    if (onComplete) {
      onComplete();
    }
  };

  const StepComponent = steps[currentStep].component;
  const progress = ((currentStep + 1) / steps.length) * 100;

  if (loading) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleSkip(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <DialogTitle className="text-2xl">Getting Started</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4 mr-1" />
              Skip Tour
            </Button>
          </div>
          <DialogDescription>
            Step {currentStep + 1} of {steps.length}: {steps[currentStep].title}
          </DialogDescription>
          <Progress value={progress} className="mt-4" />
        </DialogHeader>

        {/* Step Content */}
        <div className="py-6">
          <StepComponent
            user={user}
            data={onboardingData}
            onNext={handleNext}
            navigate={navigate}
            apiKeyCopied={apiKeyCopied}
            setApiKeyCopied={setApiKeyCopied}
          />
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button
            onClick={handleBack}
            variant="outline"
            disabled={currentStep === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`h-2 w-2 rounded-full transition-colors ${
                  index === currentStep
                    ? 'bg-teal-600 w-8'
                    : index < currentStep
                    ? 'bg-teal-300'
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {currentStep === steps.length - 1 ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Get Started
              </>
            ) : (
              <>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
